/**
 * Signature resolver — fetches, caches, and injects corporate signatures.
 *
 * Uses the /api/v1/content/signature-templates/resolve endpoint which returns
 * pre-rendered HTML personalized for the current user. Caches in storage
 * (localStorage or OfficeRuntime.Storage on Windows) with hash-based
 * invalidation and a configurable TTL.
 *
 * Cache is keyed per sender email address so that delegate and shared mailboxes
 * each get their own resolved signature independent of the logged-in user.
 */

import { config } from './config';
import { getAccessToken } from './auth';
import { trackSignatureImpression } from './tracking';
import { getItem, setItem, removeItem, storageKeys } from './storage';

interface CachedSignature {
  tenantId: string;
  templateId: string;
  hash: string;
  renderedHtml: string;
  renderedPlainText: string;
  corporateDomains: string[];
  applyToPersonalEmails: boolean;
  clickTrackingEnabled: boolean;
  /** Signature delivery mode from tenant policy. Only apply when 'OutlookAddin'. */
  deliveryMode: string;
  fetchedAt: number;
}

/** Map of normalized sender email → cached signature, stored as a single JSON blob. */
type SignatureCacheMap = Record<string, CachedSignature>;

/** Normalize an email address to a consistent cache key. */
function normalizeSenderKey(senderEmail: string | undefined): string {
  return (senderEmail ?? '').toLowerCase().trim();
}

/**
 * Resolve the signature for the given sender mailbox, using cache when available.
 *
 * Returns cached data immediately if within TTL. When cache is expired (or missing),
 * awaits a fresh fetch from the API so the compose window always gets the latest
 * deployed signature. Falls back to stale cache only on network failure.
 *
 * @param options.allowDialog - Forwarded to getAccessToken(). Set false in event-based
 *   handlers where dialog auth is unavailable. Defaults to true.
 * @param options.senderEmail - The From address of the compose item. When provided the
 *   backend returns a signature personalized for that mailbox (handles delegate/shared
 *   mailbox scenarios). Omit to fall back to the logged-in user's default signature.
 */
export async function resolveSignature(
  options?: { allowDialog?: boolean; senderEmail?: string },
): Promise<CachedSignature | null> {
  const key = normalizeSenderKey(options?.senderEmail);
  const cached = await loadCachedSignature(key);

  if (cached && !isCacheExpired(cached)) {
    // Cache is fresh — return immediately, no network call.
    return cached;
  }

  // Cache is expired or missing — fetch fresh data. The API typically responds
  // in <2s and Office.js event handlers have a 5-minute timeout, so awaiting
  // is safe. If the fetch fails, fall back to the stale cache (if any).
  const fresh = await fetchAndCacheSignature(options);
  return fresh ?? cached ?? null;
}

/** Fetch signature from the backend and update cache. */
async function fetchAndCacheSignature(
  options?: { allowDialog?: boolean; senderEmail?: string },
): Promise<CachedSignature | null> {
  const key = normalizeSenderKey(options?.senderEmail);

  try {
    const token = await getAccessToken(options);

    const url = new URL(`${config.apiBaseUrl}/content/signature-templates/resolve`);
    if (options?.senderEmail) {
      url.searchParams.set('senderEmail', options.senderEmail);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(15_000),
    });

    if (!response.ok) {
      console.warn(`[Ambient] Signature resolve failed: ${response.status}`);
      // Fall back to cache even if expired.
      return loadCachedSignature(key);
    }

    const data = await response.json() as {
      tenantId: string | null;
      templateId: string | null;
      hash: string | null;
      renderedHtml: string | null;
      renderedPlainText: string | null;
      policy: {
        corporateDomains: string[];
        applyToPersonalEmails: boolean;
        clickTrackingEnabled: boolean;
        deliveryMode?: string;
      };
    };

    if (!data.templateId || !data.renderedHtml) {
      console.info(
        '[Ambient] Signature resolve: API returned no template',
        { templateId: data.templateId, hasHtml: !!data.renderedHtml, policy: data.policy },
      );
      return null;
    }

    const cached: CachedSignature = {
      tenantId: data.tenantId ?? '',
      templateId: data.templateId,
      hash: data.hash ?? '',
      renderedHtml: data.renderedHtml,
      renderedPlainText: data.renderedPlainText ?? '',
      corporateDomains: data.policy.corporateDomains,
      applyToPersonalEmails: data.policy.applyToPersonalEmails,
      clickTrackingEnabled: data.policy.clickTrackingEnabled,
      deliveryMode: data.policy.deliveryMode ?? '',
      fetchedAt: Date.now(),
    };

    await saveCachedSignature(key, cached);
    return cached;
  } catch (err) {
    console.warn('[Ambient] Failed to fetch signature:', err);
    return loadCachedSignature(key);
  }
}

/**
 * Remove leading blank block elements from HTML so Outlook's own 2-paragraph
 * gap above the signature is the only whitespace. This prevents double-spacing
 * when signature templates include leading <br> or empty <p>/<div> elements.
 */
function trimLeadingBlankElements(html: string): string {
  // Repeatedly strip a leading blank block: <br>, <p>…</p>, <div>…</div>
  // where the content is empty, whitespace, or &nbsp; only.
  const blankBlock = /^(\s*(<br\s*\/?>|<p[^>]*>(\s|&nbsp;)*<\/p>|<div[^>]*>(\s|&nbsp;)*<\/div>)\s*)+/i;
  return html.replace(blankBlock, '');
}

/**
 * Apply a signature to the current compose item using Office.js.
 * Returns true if the signature was successfully set.
 */
export async function applySignatureToCompose(
  item: Office.MessageCompose,
  signature: CachedSignature,
): Promise<boolean> {
  const html = trimLeadingBlankElements(signature.renderedHtml);
  return new Promise((resolve) => {
    item.body.setSignatureAsync(
      html,
      { coercionType: Office.CoercionType.Html },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          trackSignatureImpression(signature.templateId, signature.tenantId);
          resolve(true);
        } else {
          console.warn('[Ambient] setSignatureAsync failed:', result.error?.message);
          resolve(false);
        }
      },
    );
  });
}

/** Clear the compose item's signature. */
export async function clearSignature(item: Office.MessageCompose): Promise<void> {
  return new Promise((resolve) => {
    item.body.setSignatureAsync(
      '',
      { coercionType: Office.CoercionType.Html },
      () => resolve(),
    );
  });
}

/** Check if the add-in should handle signature injection based on delivery mode.
 *  Normalizes all casing variants (PascalCase, camelCase, snake_case). */
export function isAddinDeliveryMode(signature: CachedSignature): boolean {
  const mode = (signature.deliveryMode ?? '').toLowerCase().replace(/_/g, '');
  return mode === 'outlookaddin';
}

/** Check if the sender email belongs to a corporate domain. */
export function isCorporateEmail(email: string, corporateDomains: string[]): boolean {
  if (corporateDomains.length === 0) return true;
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  return corporateDomains.some((d) => d.toLowerCase() === domain);
}

function isCacheExpired(cached: CachedSignature): boolean {
  return Date.now() - cached.fetchedAt > config.signatureCacheTtlMs;
}

async function loadCacheMap(): Promise<SignatureCacheMap> {
  const raw = await getItem(storageKeys.signatureCache);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    // Guard against old single-entry cache format (had a 'templateId' at the top level).
    if (typeof parsed === 'object' && parsed !== null && !('templateId' in parsed)) {
      return parsed as SignatureCacheMap;
    }
    return {};
  } catch {
    return {};
  }
}

async function saveCachedSignature(key: string, cached: CachedSignature): Promise<void> {
  const map = await loadCacheMap();
  map[key] = cached;
  await setItem(storageKeys.signatureCache, JSON.stringify(map));
}

async function loadCachedSignature(key: string): Promise<CachedSignature | null> {
  const map = await loadCacheMap();
  return map[key] ?? null;
}

/** Clear the entire signature cache, forcing fresh fetches on next resolve. */
export async function clearSignatureCache(): Promise<void> {
  await removeItem(storageKeys.signatureCache);
}

/**
 * Force-resolve a signature, bypassing the cache entirely.
 * Used by the taskpane refresh button to guarantee a fresh fetch.
 */
export async function forceResolveSignature(
  options?: { allowDialog?: boolean; senderEmail?: string },
): Promise<CachedSignature | null> {
  await clearSignatureCache();
  return fetchAndCacheSignature(options);
}
